import { useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/hooks/useAuth';
import { useCapabilities } from '@/hooks/useCapabilities';
import { SidebarUploadPanel } from '@/components/recording/SidebarUploadPanel';
import {
  HomeIcon,
  CalendarIcon,
  UserGroupIcon,
  MicrophoneIcon,
  CogIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentListIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Sparkles, BarChart3 } from 'lucide-react';

// =============================================================================
// Types & Interfaces
// =============================================================================

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  permission: string | null;
  visible?: boolean;
}

interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile: boolean;
  isExpanded: boolean;
  onToggleExpand: () => void;
}

// =============================================================================
// Animation Variants
// =============================================================================

const sidebarVariants = {
  collapsed: {
    width: 64,
    transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
  },
  expanded: {
    width: 240,
    transition: { duration: 0.25, ease: [0.4, 0, 0.2, 1] }
  },
  mobileOpen: {
    x: 0,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  },
  mobileClosed: {
    x: -340,
    transition: { duration: 0.3, ease: [0.4, 0, 0.2, 1] }
  }
};

const labelVariants = {
  hidden: {
    opacity: 0,
    width: 0,
    marginLeft: 0,
    transition: { duration: 0.15 }
  },
  visible: {
    opacity: 1,
    width: 'auto',
    marginLeft: 12,
    transition: { duration: 0.2, delay: 0.05 }
  }
};

const sectionHeaderVariants = {
  hidden: {
    opacity: 0,
    height: 0,
    marginBottom: 0,
    transition: { duration: 0.15 }
  },
  visible: {
    opacity: 1,
    height: 'auto',
    marginBottom: 4,
    transition: { duration: 0.2, delay: 0.1 }
  }
};

// =============================================================================
// Sub-Components
// =============================================================================

// Logo Section Component
const LogoSection: React.FC<{
  isExpanded: boolean;
  isMobile: boolean;
  onToggleExpand: () => void;
}> = ({ isExpanded, isMobile, onToggleExpand }) => (
  <div className="relative flex items-center justify-between px-3 py-3 border-b border-gray-100">
    <NavLink to="/dashboard" className="flex items-center gap-3 group">
      {/* Logo Icon */}
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow duration-200 flex-shrink-0">
        <img src="/images/amplify-favicon.png" alt="Amplify" className="w-full h-full object-contain p-1" />
      </div>

      {/* Logo Text */}
      <AnimatePresence mode="wait">
        {(isExpanded || isMobile) && (
          <motion.div
            variants={labelVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="flex flex-col overflow-hidden"
          >
            <span className="text-[15px] font-semibold text-gray-900 tracking-tight leading-tight">
              Amplify
            </span>
            <span className="text-[10px] text-gray-500 leading-tight">
              by CareTracker
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </NavLink>

    {/* Toggle Button - Desktop only */}
    {!isMobile && (
      <motion.button
        onClick={(e) => {
          e.stopPropagation();
          onToggleExpand();
        }}
        className={`
          flex items-center justify-center transition-all duration-200
          ${isExpanded
            ? 'w-7 h-7 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            : 'absolute -right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white shadow-lg border border-gray-200 text-gray-500 hover:text-gray-700 hover:shadow-xl hover:scale-110'
          }
        `}
        whileTap={{ scale: 0.9 }}
        title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {isExpanded ? (
          <ChevronLeftIcon className="w-4 h-4" />
        ) : (
          <ChevronRightIcon className="w-4 h-4" />
        )}
      </motion.button>
    )}
  </div>
);

// Navigation Item Component
const NavigationItem: React.FC<{
  item: NavItem;
  isExpanded: boolean;
  isMobile: boolean;
  onNavigate: () => void;
}> = ({ item, isExpanded, isMobile, onNavigate }) => {
  const showLabels = isExpanded || isMobile;

  return (
    <NavLink
      to={item.href}
      onClick={onNavigate}
      className={({ isActive }) => `
        group relative flex items-center rounded-lg transition-all duration-200
        ${showLabels ? 'px-4 py-3 gap-3' : 'px-0 py-2.5 justify-center'}
        ${isActive
          ? 'bg-[#1e4a7a]/10 text-[#1e4a7a]'
          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }
      `}
    >
      {({ isActive }) => (
        <>
          {/* Icon */}
          <item.icon
            className={`
              w-5 h-5 flex-shrink-0 transition-colors duration-200
              ${isActive ? 'text-[#1e4a7a]' : 'text-gray-500 group-hover:text-gray-700'}
            `}
          />

          {/* Label */}
          <AnimatePresence mode="wait">
            {showLabels && (
              <motion.span
                variants={labelVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className={`
                  text-sm font-medium whitespace-nowrap overflow-hidden
                  ${isActive ? 'text-[#1e4a7a]' : 'text-gray-700 group-hover:text-gray-900'}
                `}
              >
                {item.name}
              </motion.span>
            )}
          </AnimatePresence>

          {/* Tooltip for collapsed state - using fixed positioning to escape overflow */}
          {!showLabels && (
            <div
              className="
                fixed left-16 px-2.5 py-1.5 rounded-md
                bg-gray-900 text-white text-xs font-medium
                opacity-0 invisible group-hover:opacity-100 group-hover:visible
                transition-all duration-200 whitespace-nowrap z-[9999]
                shadow-lg pointer-events-none
              "
              style={{ marginTop: '-14px' }}
            >
              {item.name}
              {/* Tooltip arrow */}
              <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
            </div>
          )}
        </>
      )}
    </NavLink>
  );
};

// Navigation Section Component
const NavigationSection: React.FC<{
  section: NavSection;
  isExpanded: boolean;
  isMobile: boolean;
  onNavigate: () => void;
}> = ({ section, isExpanded, isMobile, onNavigate }) => {
  const showLabels = isExpanded || isMobile;

  return (
    <div className="mb-2">
      {/* Section Header - hidden on mobile to save vertical space */}
      <AnimatePresence mode="wait">
        {isExpanded && !isMobile && (
          <motion.div
            variants={sectionHeaderVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="px-3 py-2"
          >
            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {section.title}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Section Items */}
      <div className={`space-y-0.5 ${!showLabels ? 'px-2' : ''}`}>
        {section.items.map((item) => (
          <NavigationItem
            key={item.href}
            item={item}
            isExpanded={isExpanded}
            isMobile={isMobile}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </div>
  );
};

// User Section Component
const UserSection: React.FC<{
  isExpanded: boolean;
  isMobile: boolean;
}> = ({ isExpanded, isMobile }) => {
  const { user, logout } = useAuth();
  const [showMenu, setShowMenu] = useState(false);
  const showLabels = isExpanded || isMobile;

  if (!user) return null;

  const initials = `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase();
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  const roleDisplay = user.role?.charAt(0).toUpperCase() + user.role?.slice(1);

  const handleSignOut = async () => {
    setShowMenu(false);
    await logout();
  };

  return (
    <div className={`
      border-t border-gray-100 p-3 relative
      ${showLabels ? '' : 'flex justify-center'}
    `}>
      {/* User Button */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className={`
          w-full flex items-center gap-3 rounded-lg
          ${showLabels ? 'p-2 hover:bg-gray-50' : 'p-0 justify-center'}
          transition-colors duration-200 group relative
        `}
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#1e4a7a] to-[#16a085] flex items-center justify-center text-white text-xs font-semibold shadow-sm flex-shrink-0">
          {initials || 'U'}
        </div>

        {/* User Info */}
        <AnimatePresence mode="wait">
          {showLabels && (
            <motion.div
              variants={labelVariants}
              initial="hidden"
              animate="visible"
              exit="hidden"
              className="flex flex-col overflow-hidden min-w-0 text-left flex-1"
            >
              <span className="text-sm font-medium text-gray-900 truncate">
                {fullName || 'User'}
              </span>
              <span className="text-[11px] text-gray-500 truncate">
                {roleDisplay}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chevron indicator when expanded */}
        {showLabels && (
          <ChevronRightIcon className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${showMenu ? 'rotate-90' : ''}`} />
        )}

        {/* Tooltip for collapsed state - using fixed positioning */}
        {!showLabels && !showMenu && (
          <div
            className="
              fixed left-16 px-2.5 py-1.5 rounded-md
              bg-gray-900 text-white text-xs font-medium
              opacity-0 invisible group-hover:opacity-100 group-hover:visible
              transition-all duration-200 whitespace-nowrap z-[9999]
              shadow-lg pointer-events-none
            "
            style={{ marginTop: '-20px' }}
          >
            {fullName}
            <div className="text-gray-400 text-[10px]">{roleDisplay}</div>
            <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45" />
          </div>
        )}
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {showMenu && (
          <>
            {/* Backdrop to close menu */}
            <div
              className="fixed inset-0 z-[90]"
              onClick={() => setShowMenu(false)}
            />

            {/* Menu */}
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className={`
                absolute z-[100] bg-white rounded-lg shadow-xl border border-gray-200
                py-1 min-w-[160px]
                ${showLabels
                  ? 'bottom-full mb-2 left-3 right-3'
                  : 'left-full ml-3 bottom-0'
                }
              `}
            >
              {/* User info in menu */}
              <div className="px-3 py-2 border-b border-gray-100">
                <p className="text-sm font-medium text-gray-900 truncate">{fullName}</p>
                <p className="text-xs text-gray-500 truncate">{user.email}</p>
              </div>

              {/* My Profile link */}
              <NavLink
                to="/profile"
                onClick={() => setShowMenu(false)}
                className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                My Profile
              </NavLink>

              {/* Sign Out button */}
              <button
                onClick={handleSignOut}
                className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Sign out
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

// =============================================================================
// Main Sidebar Component
// =============================================================================

const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  onClose,
  isMobile,
  isExpanded,
  onToggleExpand
}) => {
  const { hasPermission, canAccessPlatform } = useAuth();
  const { capabilities } = useCapabilities();
  // Platform roles (superadmin, enterprise/partner admins) must not see the
  // clinical Settings item, even though partner admins hold organization.settings.modify.
  const isPlatformRole = canAccessPlatform();

  // Navigation configuration with sections
  const navigationSections: NavSection[] = useMemo(() => [
    {
      id: 'clinical',
      title: 'Clinical',
      items: [
        {
          name: 'Dashboard',
          href: '/dashboard',
          icon: HomeIcon,
          permission: null,
          // Unified provider homepage now serves both modes, so the nav item shows in both.
          visible: true
        },
        {
          name: 'Encounters',
          href: '/encounters',
          icon: ClipboardDocumentListIcon,
          permission: 'encounters.read',
          visible: capabilities?.ui.showEncounters
        },
        {
          name: 'Recording',
          href: '/recording',
          icon: MicrophoneIcon,
          permission: 'recordings.create',
          // Standalone records against appointments; connected records inside an encounter,
          // so the standalone-only Recording entry is hidden in connected (encounters-only) mode.
          // AMP-136: hidden from users who may not record (standalone org admins).
          visible: capabilities?.ui.dashboardMode === 'standard' && capabilities?.recording?.canRecord !== false
        },
        {
          name: 'Appointments',
          href: '/appointments',
          icon: CalendarIcon,
          permission: 'patients.phi.view',
          visible: capabilities?.ui.showAppointments
        },
        {
          name: 'Patients',
          href: '/patients',
          icon: UserGroupIcon,
          permission: 'patients.phi.view',
          visible: capabilities?.ui.showPatients
        },
      ]
    },
    {
      id: 'tools',
      title: 'Tools',
      items: [
        {
          name: 'Templates',
          href: '/templates',
          icon: DocumentDuplicateIcon,
          permission: 'clinical.edit',
          visible: capabilities?.ui.showTemplates
        },
        {
          name: 'My Usage',
          href: '/my-usage',
          icon: BarChart3,
          permission: 'usage.own.view',
          // Hidden for now: the /my-usage page has an infinite fetch loop
          // (MyUsagePage + UsageLimitBanner remount cycle). Re-enable once fixed.
          visible: false
        },
        {
          name: 'Settings',
          href: '/settings',
          icon: CogIcon,
          permission: 'organization.settings.modify',
          visible: !isPlatformRole
        },
      ]
    }
  ], [capabilities, isPlatformRole]);

  // Filter sections based on permissions
  const filteredSections = useMemo(() =>
    navigationSections
      .map(section => ({
        ...section,
        items: section.items.filter(item =>
          (!item.permission || hasPermission(item.permission)) &&
          (item.visible === true)
        )
      }))
      .filter(section => section.items.length > 0),
    [navigationSections, hasPermission]
  );

  // Handle navigation click on mobile
  const handleNavClick = () => {
    if (isMobile) {
      onClose();
    }
  };

  // Determine animation state
  const getAnimationState = () => {
    if (isMobile) {
      return isOpen ? 'mobileOpen' : 'mobileClosed';
    }
    return isExpanded ? 'expanded' : 'collapsed';
  };

  return (
    <motion.aside
      initial={isMobile ? 'mobileClosed' : (isExpanded ? 'expanded' : 'collapsed')}
      animate={getAnimationState()}
      variants={sidebarVariants}
      className={`
        fixed left-0 bg-white border-r border-gray-200
        flex flex-col
        ${isMobile ? 'top-14 h-[calc(100vh-3.5rem)] z-50 shadow-2xl overflow-hidden' : 'top-0 h-screen z-30 shadow-sm overflow-visible'}
      `}
      style={{
        width: isMobile ? 320 : undefined
      }}
    >
      {/* Logo Section */}
      <LogoSection
        isExpanded={isExpanded}
        isMobile={isMobile}
        onToggleExpand={onToggleExpand}
      />

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3 px-2">
        {filteredSections.map((section) => (
          <NavigationSection
            key={section.id}
            section={section}
            isExpanded={isExpanded}
            isMobile={isMobile}
            onNavigate={handleNavClick}
          />
        ))}
      </nav>

      {/* Upload progress - docked above the user chip, follows across clinical routes */}
      <SidebarUploadPanel isExpanded={isExpanded} isMobile={isMobile} />

      {/* User Section */}
      <UserSection
        isExpanded={isExpanded}
        isMobile={isMobile}
      />
    </motion.aside>
  );
};

export default Sidebar;
